import { FC, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TableContainer,
  TableHead,
  TableRow,
  Table,
  TableCell,
  TableBody,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { useQuery } from '@apollo/client';
import LoadingSpinner from '../../../LoadingSpinner';
import { GET_GUIDE_AND_LINKS } from '../../guide.graphql';
import Fuse from 'fuse.js';
import { SearchField } from '../../../components/SearchField';
import { StatusChip, TagsCell } from '../../GuideStatusCells';
import React from 'react';
import { Modal } from '../../../dialogs/useModal';
import { useGuideCmsContext } from '../GuideCmsContext';
import { GuideStatus } from '../../../__generated__/graphql';

export interface GuideLinkDialogResult {
  guideId: string;
  docId: number;
  docName: string;
}

export interface GuideLinkDialogProps {
  guideId: string;
  selectedText?: string;
}

export const GuideLinkDialog: FC<Modal<GuideLinkDialogProps, GuideLinkDialogResult>> = (modal) => {
  const guideId = modal.data.guideId;
  const { close } = modal;
  const { editor } = useGuideCmsContext();
  const [filter, setFilter] = useState(modal.data.selectedText ?? '');
  const [showExpired, setShowExpired] = useState<boolean>(false);

  const { data, loading } = useQuery(GET_GUIDE_AND_LINKS, {
    variables: { id: guideId },
  });

  const handleOnClose = () => {
    close();
  };

  const filteredGuides = useMemo(() => {
    if (!data || !data.guide || !data.guides) return [];
    let filtered = data.guides.filter((x) => x && (showExpired || x.status !== GuideStatus.Expired) && x.docName);
    if (!filter) return filtered;

    var options = {
      shouldSort: false,
      tokenize: true,
      matchAllTokens: true,
      threshold: 0,
      keys: ['docTitle', 'docName', 'mainCategory.title'],
    };

    var fuse = new Fuse(filtered, options);
    filtered = fuse.search(filter.trim()).map((x) => x.item);
    return filtered;
  }, [data?.guide, data?.guides, filter, showExpired]);

  const addGuideLink = (payload: GuideLinkDialogResult) => {
    editor
      ?.chain()
      .focus()
      .insertContent({
        type: 'guideLink',
        attrs: {
          guideId: payload.guideId,
          docId: payload.docId,
          docName: payload.docName,
        },
      })
      .run();
  };

  return (
    <Dialog open={true} onClose={handleOnClose} disableRestoreFocus maxWidth="lg" fullWidth={true}>
      <DialogTitle>
        Legg til lenke til anvisning
        <FormGroup>
          <FormControlLabel control={<Checkbox onChange={(e) => setShowExpired(e.target.checked)} checked={showExpired} />} label="Vis utgåtte" />
        </FormGroup>
        <SearchField filter={setFilter} startSearch={filter} />
      </DialogTitle>
      <DialogContent>
        {loading && <LoadingSpinner />}
        {!loading && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Velg</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Nummer</TableCell>
                  <TableCell>Tittel</TableCell>
                  <TableCell>Fagområde</TableCell>
                  <TableCell>Tagger</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredGuides.map((guide) => {
                  if (!guide) return null;
                  return (
                    <TableRow key={guide.docName}>
                      <TableCell>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => {
                            const result: GuideLinkDialogResult = {
                              guideId: guide.id,
                              docId: guide.docId,
                              docName: guide.docName!,
                            };
                            addGuideLink(result);
                            close(result);
                          }}>
                          Velg
                        </Button>
                      </TableCell>
                      <TableCell>
                        <StatusChip guide={guide} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{guide.docName}</TableCell>
                      <TableCell>{guide.docTitle}</TableCell>
                      <TableCell>{guide.mainCategory?.title}</TableCell>
                      <TableCell>
                        <TagsCell guide={guide} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleOnClose}>Avbryt</Button>
      </DialogActions>
    </Dialog>
  );
};
